/**
 * useHomeSLDemoComposeContentLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeSLDemoComposeContent
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeSLDemoComposeLayout, HomeSLDemoComposeContentEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeSLDemoComposeContentState =
  | 'idle';

export type HomeSLDemoComposeContentEvent =
  | 'INIT'
  | 'ADD_BLOCK'
  | 'TOUR_STEP'
  | 'TOUR_RESET';

type HomeSLDemoComposeContentSlot = 'main';
interface HomeSLDemoComposeContentInternalState {
  machineState: HomeSLDemoComposeContentState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLDemoComposeContentEvent | null;
  slotWriter: Partial<Record<HomeSLDemoComposeContentSlot, HomeSLDemoComposeContentEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLDemoComposeContentEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLDemoComposeLayout>;
}

type HomeSLDemoComposeContentAction =
  | { type: 'EVENT_SUCCESS'; event: HomeSLDemoComposeContentEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeSLDemoComposeContentSlot[]; renderClear?: HomeSLDemoComposeContentSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeSLDemoComposeContentEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeSLDemoComposeLayout & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeSLDemoComposeContentReducer(
  state: HomeSLDemoComposeContentInternalState,
  action: HomeSLDemoComposeContentAction
): HomeSLDemoComposeContentInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeSLDemoComposeContentState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeSLDemoComposeLayout>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeSLDemoComposeLayout>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeSLDemoComposeLayout>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeSLDemoComposeContentInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeSLDemoComposeContentConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  body: { animateChanges: boolean; children: { children: Array<Array<Array<Array<number | string> | string | { children: FieldValue[]; gap: string; type: string }> | Array<number | string> | string | { align: string; children: Array<string | { children: string[]; padding: string; type: string }>; gap: string; ratio: string; type: string }> | Array<Array<number | string> | string | { children: { color: string; content: string[]; type: string; variant: string }[]; className: string; type: string }> | { children: Array<{ color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string }>; direction: string; gap: string; type: string }>; direction: string; gap: string; type: string }[]; className: string; type: string };
}

const DEFAULT_HOME_SL_DEMO_COMPOSE_CONTENT_CONFIG = {
  body: { animateChanges: true, children: [{ children: [{ children: [{ content: ["i18n/t", "std-almadar-demo-compose:page.title"], type: "typography", variant: "h3" }, { color: "muted", content: ["i18n/t", "std-almadar-demo-compose:page.subtitle"], type: "typography", variant: "small" }], direction: "vertical", gap: "none", type: "stack" }, ["if", [">=", "@entity.added", 3], { align: "stretch", children: ["@trait.HomeSLDemoComposeStats", { children: ["@trait.HomeSLDemoComposeStageChart"], padding: "sm", type: "card" }], gap: "md", ratio: "3:1", type: "split" }, ["if", [">=", "@entity.added", 2], "@trait.HomeSLDemoComposeStats", { children: [], gap: "none", type: "stack" }]], ["if", ["=", "@entity.added", 0], { children: [{ color: "muted", content: ["i18n/t", "std-almadar-demo-compose:compose.empty"], type: "typography", variant: "body" }], className: "h-96 rounded-xl border-2 border-dashed border-border flex items-center justify-center", type: "box" }, "@trait.HomeSLDemoComposeBoard"]], direction: "vertical", gap: "sm", type: "stack" }], className: "px-1 py-3", type: "box" },
} as HomeSLDemoComposeContentConfig;

export interface HomeSLDemoComposeContentLogicReturn {
  state: HomeSLDemoComposeContentState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLDemoComposeContentEvent | null;
  slotWriter: Partial<Record<HomeSLDemoComposeContentSlot, HomeSLDemoComposeContentEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLDemoComposeContentEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLDemoComposeLayout>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeSLDemoComposeContentEventPayloadMap]: (payload?: HomeSLDemoComposeContentEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeSLDemoComposeContentConfig;
}

const __CASCADE_EVENTS = new Set<string>(['ADD_BLOCK', 'TOUR_RESET', 'TOUR_STEP']);

export function useHomeSLDemoComposeContentLogic(config?: Partial<HomeSLDemoComposeContentConfig>): HomeSLDemoComposeContentLogicReturn {
  const mergedConfig: HomeSLDemoComposeContentConfig = { ...DEFAULT_HOME_SL_DEMO_COMPOSE_CONTENT_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-sl-demo-compose-content', orbitalName: 'HomeSL', traitName: 'HomeSLDemoComposeContent' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeSLDemoComposeContentReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeSLDemoComposeLayout'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        _fields['added'] = 0;
        fields = { ...(fields ?? {}), 'added': _fields['added'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'ADD_BLOCK') {
      // idle → idle (on ADD_BLOCK)
        _fields['added'] = payloadObj?.count;
        fields = { ...(fields ?? {}), 'added': _fields['added'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeSLDemoComposeContentSlot[]; clear: HomeSLDemoComposeContentSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'ADD_BLOCK': return { write: ['main'] as HomeSLDemoComposeContentSlot[], clear: [] as HomeSLDemoComposeContentSlot[] };
          case 'INIT': return { write: ['main'] as HomeSLDemoComposeContentSlot[], clear: [] as HomeSLDemoComposeContentSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeSLDemoComposeLayout': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeSLDemoComposeContentEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'HomeSLDemoComposeContent': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeSLDemoComposeContentEventPayloadMap]: (payload?: HomeSLDemoComposeContentEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'ADD_BLOCK': (payload?: HomeSLDemoComposeContentEventPayloadMap['ADD_BLOCK']) => dispatch('ADD_BLOCK', payload),
  };

  useUIEvents(enqueueEvent, 'HomeSL.HomeSLDemoComposeContent', ['INIT', 'ADD_BLOCK'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeSLDemoComposeContent', (): TraitStateSnapshot => ({
      traitName: 'HomeSLDemoComposeContent',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'ADD_BLOCK'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOUR_STEP' (from HomeSLDemoComposeTour) → trigger 'ADD_BLOCK'
    const unsub_tour_step_from_homesldemocomposetour_to_add_block = eventBus.on('UI:HomeSL.HomeSLDemoComposeTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "ADD_BOARD"))) return;
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoComposeContent', 'ADD_BLOCK')) return;
      enqueueEvent('ADD_BLOCK', { count: 1 } as EventPayload);
    });
    // Listen for 'TOUR_STEP' (from HomeSLDemoComposeTour) → trigger 'ADD_BLOCK'
    const unsub_tour_step_from_homesldemocomposetour_to_add_block_2 = eventBus.on('UI:HomeSL.HomeSLDemoComposeTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "ADD_KPIS"))) return;
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoComposeContent', 'ADD_BLOCK')) return;
      enqueueEvent('ADD_BLOCK', { count: 2 } as EventPayload);
    });
    // Listen for 'TOUR_STEP' (from HomeSLDemoComposeTour) → trigger 'ADD_BLOCK'
    const unsub_tour_step_from_homesldemocomposetour_to_add_block_3 = eventBus.on('UI:HomeSL.HomeSLDemoComposeTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "ADD_CHART"))) return;
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoComposeContent', 'ADD_BLOCK')) return;
      enqueueEvent('ADD_BLOCK', { count: 3 } as EventPayload);
    });
    // Listen for 'TOUR_RESET' (from HomeSLDemoComposeTour) → trigger 'ADD_BLOCK'
    const unsub_tour_reset_from_homesldemocomposetour_to_add_block = eventBus.on('UI:HomeSL.HomeSLDemoComposeTour.TOUR_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoComposeContent', 'ADD_BLOCK')) return;
      enqueueEvent('ADD_BLOCK', { count: 0 } as EventPayload);
    });
    return () => {
      if (typeof unsub_tour_step_from_homesldemocomposetour_to_add_block === 'function') unsub_tour_step_from_homesldemocomposetour_to_add_block();
      if (typeof unsub_tour_step_from_homesldemocomposetour_to_add_block_2 === 'function') unsub_tour_step_from_homesldemocomposetour_to_add_block_2();
      if (typeof unsub_tour_step_from_homesldemocomposetour_to_add_block_3 === 'function') unsub_tour_step_from_homesldemocomposetour_to_add_block_3();
      if (typeof unsub_tour_reset_from_homesldemocomposetour_to_add_block === 'function') unsub_tour_reset_from_homesldemocomposetour_to_add_block();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
